import { App } from '@capacitor/app'
import { getPlatform } from '@/lib/platform/platformDetection'

/**
 * Who is calling: three headers sent on every request this app makes to its own API, so the
 * backend can count the clients that still run a build older than a change it wants to ship
 * (AUTHZ-02 E2, decision 3). A request WITHOUT them comes from a pre-E2 build — that absence is
 * what E3 counts, which is why every API path must send them and why
 * `__tests__/apiFetch.inventory.test.ts` fails on a raw `fetch` to the API.
 *
 *  - `X-Client-Build`    — the web build id (`__BUILD_ID__`, the deployed version.json hash).
 *  - `X-Client-Version`  — the installed app version on native (Capacitor `App.getInfo()`),
 *                          the package version on web, where every tab runs what was deployed.
 *  - `X-Client-Platform` — `web` | `ios-native` | `android-native`.
 *
 * The headers are ALWAYS present. A value that cannot be read is sent as `unknown` rather than
 * dropped, because a dropped header would count this client as pre-E2.
 *
 * Identity only — never a credential. The backend must not authorize anything on these values.
 */
export const CLIENT_BUILD_HEADER = 'X-Client-Build'
export const CLIENT_VERSION_HEADER = 'X-Client-Version'
export const CLIENT_PLATFORM_HEADER = 'X-Client-Platform'

export const UNKNOWN_CLIENT_VALUE = 'unknown'

/**
 * How long the FIRST request on native waits for `App.getInfo()`. Bounded because the bridge can
 * stall during a cold start, and a request must not hang on a header. Paid once: later requests
 * never wait — they use the cached value, or `unknown` while the read is still out.
 */
export const NATIVE_VERSION_WAIT_MS = 1000

const IDENTITY_HEADER_NAMES = new Set(
  [CLIENT_BUILD_HEADER, CLIENT_VERSION_HEADER, CLIENT_PLATFORM_HEADER].map(name =>
    name.toLowerCase(),
  ),
)

type NativeVersionRead = {
  /** Settles when the bounded first wait is over, whether or not the read finished. */
  boundedWait: Promise<void>
  waitOver: boolean
}

let cachedNativeVersion: string | null = null
let nativeVersionRead: NativeVersionRead | null = null

function startNativeVersionRead(): NativeVersionRead {
  const read = App.getInfo().then(
    info => {
      if (typeof info?.version === 'string' && info.version !== '') {
        cachedNativeVersion = info.version
      }
    },
    () => undefined,
  )

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<void>(resolve => {
    timer = setTimeout(resolve, NATIVE_VERSION_WAIT_MS)
  })

  const state: NativeVersionRead = {
    boundedWait: Promise.race([read, timeout]).then(() => {
      clearTimeout(timer)
      state.waitOver = true
    }),
    waitOver: false,
  }

  // A read that FINISHED without a version (rejected, or an empty answer) is forgotten, so the
  // next request asks again; one that never finishes stays pending and is never waited on twice.
  void read.then(() => {
    if (nativeVersionRead === state) nativeVersionRead = null
  })

  return state
}

async function nativeAppVersion(): Promise<string> {
  if (cachedNativeVersion !== null) return cachedNativeVersion

  nativeVersionRead ??= startNativeVersionRead()
  const read = nativeVersionRead
  if (!read.waitOver) await read.boundedWait

  return cachedNativeVersion ?? UNKNOWN_CLIENT_VALUE
}

/** The three identity headers for the request about to be sent. */
export async function clientIdentityHeaders(): Promise<Record<string, string>> {
  const platform = getPlatform()
  const version = platform === 'web' ? __APP_VERSION__ : await nativeAppVersion()

  return {
    [CLIENT_BUILD_HEADER]: __BUILD_ID__ || UNKNOWN_CLIENT_VALUE,
    [CLIENT_VERSION_HEADER]: version || UNKNOWN_CLIENT_VALUE,
    [CLIENT_PLATFORM_HEADER]: platform,
  }
}

/** `HeadersInit`, named through `Headers` because the lint config does not know DOM type globals. */
type HeadersInput = NonNullable<ConstructorParameters<typeof Headers>[0]>

export function isClientIdentityHeader(name: string): boolean {
  return IDENTITY_HEADER_NAMES.has(name.toLowerCase())
}

/**
 * Duck-typed rather than `instanceof Headers`: a `Headers` from another realm (a `Request` built by
 * a different global, as under jsdom) fails `instanceof`, and as a plain object it has no own
 * entries — every header on it would be silently dropped.
 */
function isHeadersLike(headers: HeadersInput | undefined): headers is Headers {
  return (
    typeof headers === 'object' &&
    headers !== null &&
    !Array.isArray(headers) &&
    typeof (headers as Headers).forEach === 'function' &&
    typeof (headers as Headers).get === 'function'
  )
}

/**
 * `headers` with the identity headers set, in the SAME shape it came in: a plain object stays a
 * plain object (the offline queue reads and edits replay headers as one), a `Headers` stays a
 * `Headers`, tuples stay tuples. The input is never mutated.
 *
 * Identity wins over anything already there, matched case-insensitively. A replayed queue entry
 * carries the headers stored when it was enqueued, possibly by an older build; the request is
 * sent by THIS build, so this build's identity is the true one.
 */
export function withClientIdentity(
  headers: HeadersInput | undefined,
  identity: Record<string, string>,
): HeadersInput {
  if (isHeadersLike(headers)) {
    const merged = new Headers()
    headers.forEach((value, name) => merged.set(name, value))
    for (const [name, value] of Object.entries(identity)) merged.set(name, value)
    return merged
  }

  if (Array.isArray(headers)) {
    return [
      ...headers.filter(([name]) => !isClientIdentityHeader(name)),
      ...Object.entries(identity),
    ]
  }

  const kept = Object.fromEntries(
    Object.entries(headers ?? {}).filter(([name]) => !isClientIdentityHeader(name)),
  )
  return { ...kept, ...identity }
}

/** Test seam: forget the cached native version and any read in flight. */
export function resetClientIdentityForTests(): void {
  cachedNativeVersion = null
  nativeVersionRead = null
}
